// Drill-down de casos por agente: panel lateral que abre la fila de
// AgentTable con el listado plano de casos (sellados + vigente) del agente en
// el rango del reporte, servido por GET /helpdesk/reports/cases.
//
// NOTA DE CUADRE: el filtro agent_id del backend es assigned_to O resolved_by,
// la misma población que alimenta la fila de AgentTable. Un caso asignado Y
// resuelto por el mismo agente es UNA fila aquí aunque sume en las columnas
// "Asignadas" y "Resueltas" del dashboard: por eso el total del drawer va
// entre max(asignadas, resueltas) y su suma — no es un descuadre.

import { memo, useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Download, Loader2, AlertTriangle } from "lucide-react";
import { cn } from "../../../../utils/cn";
import { Drawer } from "../../../../components/ui/Drawer";
import { Skeleton } from "../../../../components/ui/Skeleton";
import { PendingBackendNotice } from "../../../../components/ui/PendingBackendNotice";
import { useAgentNames } from "./useAgentNames";
import { getReportCases, downloadCasesCsv, isEndpointMissing } from "./reportsApi";
import { formatDateTime, formatMinutes, formatInt, formatRangeLabel, localYMD } from "./format";

const PAGE_SIZE = 50;

// Mismo criterio de canal que el resto del helpdesk: Evolution (whatsapp no
// oficial) se presenta como WhatsApp.
const CHANNEL_LABELS = {
  whatsapp: "WhatsApp",
  whatsapp_unofficial: "WhatsApp",
  messenger: "Messenger",
  instagram: "Instagram",
  portal: "Portal",
};

function channelLabel(channel) {
  if (!channel) return "—";
  return CHANNEL_LABELS[channel] || channel.charAt(0).toUpperCase() + channel.slice(1);
}

// ISO → etiqueta de rango legible reutilizando el formateador del header del
// reporte (los props del drawer son solo primitivos: el YMD se deriva acá).
function rangeLabelFromISO(fromISO, toISO) {
  if (!fromISO || !toISO) return "";
  return formatRangeLabel(localYMD(new Date(fromISO)), localYMD(new Date(toISO)));
}

const HEADER_CELL_CLS = cn(
  // sticky va en cada th (no en thead: soporte inconsistente); el borde viaja
  // con la celda para que no desaparezca al scrollear.
  "sticky top-0 z-10 bg-bg text-[12px] md:text-[11px] font-semibold uppercase tracking-wide text-fg-muted",
  "py-2 px-3 border-b border-border whitespace-nowrap",
);

const SECONDARY_BTN_CLS = cn(
  "inline-flex items-center justify-center gap-1.5 h-11 md:h-8 px-3 rounded-md text-xs font-medium",
  "border border-border bg-transparent text-fg hover:bg-bg-subtle",
  "focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 transition-colors",
  "disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-transparent",
);

/**
 * @param {object} props
 * @param {boolean} props.open
 * @param {string|null} props.agentId
 * @param {string} props.fromISO
 * @param {string} props.toISO
 * @param {() => void} props.onClose - estable (useCallback del contenedor)
 */
function AgentCasesDrawer({ open, agentId, fromISO, toISO, onClose }) {
  const { t } = useTranslation();
  const { agentNameOf } = useAgentNames();

  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [loadError, setLoadError] = useState(null);
  const [backendMissing, setBackendMissing] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);
  const [csvState, setCsvState] = useState("idle"); // idle|downloading|error

  // Guard de carreras (patrón reqRef de HelpdeskReports): al cambiar de agente
  // o rango con una respuesta en vuelo, la vieja no debe pisar la nueva.
  const reqRef = useRef(0);
  const csvTimerRef = useRef(null);

  useEffect(() => () => clearTimeout(csvTimerRef.current), []);

  useEffect(() => {
    // Cerrado NO se dispara ningún request: el drawer vive montado siempre en
    // HelpdeskReports y solo trabaja al abrirse.
    if (!open || !agentId) return undefined;
    let alive = true;
    const id = ++reqRef.current;
    // Cambio de agente/rango: se parte de cero (rows y offset).
    setRows([]);
    setTotal(0);
    setLoading(true);
    setLoadError(null);
    setBackendMissing(false);

    (async () => {
      try {
        const res = await getReportCases({
          from: fromISO,
          to: toISO,
          agent_id: agentId,
          limit: PAGE_SIZE,
          offset: 0,
        });
        if (!alive || reqRef.current !== id) return;
        setRows(res?.rows || []);
        setTotal(res?.total || 0);
      } catch (err) {
        if (!alive || reqRef.current !== id) return;
        if (isEndpointMissing(err)) setBackendMissing(true);
        else setLoadError(err?.message || t("No se pudieron cargar los casos."));
      } finally {
        if (alive && reqRef.current === id) setLoading(false);
      }
    })();

    return () => {
      alive = false;
    };
  }, [open, agentId, fromISO, toISO, reloadKey, t]);

  const handleRetry = useCallback(() => setReloadKey((k) => k + 1), []);

  // "Cargar más": APPEND con offset = filas ya cargadas; el spinner va en el
  // botón, la tabla no vuelve al skeleton ni resetea su scroll.
  const rowsLen = rows.length;
  const handleLoadMore = useCallback(async () => {
    const id = reqRef.current;
    setLoadingMore(true);
    try {
      const res = await getReportCases({
        from: fromISO,
        to: toISO,
        agent_id: agentId,
        limit: PAGE_SIZE,
        offset: rowsLen,
      });
      if (reqRef.current !== id) return; // cambió el agente/rango en vuelo
      setRows((prev) => [...prev, ...(res?.rows || [])]);
      setTotal((prev) => res?.total ?? prev);
    } catch (err) {
      if (reqRef.current !== id) return;
      if (isEndpointMissing(err)) setBackendMissing(true);
      else setLoadError(err?.message || t("No se pudieron cargar los casos."));
    } finally {
      if (reqRef.current === id) setLoadingMore(false);
    }
  }, [fromISO, toISO, agentId, rowsLen, t]);

  const handleCsv = useCallback(async () => {
    setCsvState("downloading");
    try {
      // Mismos filtros que el listado: el CSV del drawer es SOLO este agente.
      await downloadCasesCsv({ from: fromISO, to: toISO, agent_id: agentId });
      setCsvState("idle");
    } catch {
      setCsvState("error");
      clearTimeout(csvTimerRef.current);
      csvTimerRef.current = setTimeout(() => setCsvState("idle"), 4000);
    }
  }, [fromISO, toISO, agentId]);

  const showTable = !backendMissing && !loadError;
  const hasMore = showTable && !loading && rowsLen < total;

  return (
    <Drawer
      open={open}
      onClose={onClose}
      size="lg"
      title={agentId ? agentNameOf(agentId) : ""}
      description={rangeLabelFromISO(fromISO, toISO)}
    >
      <div className="space-y-4">
        {/* Contador + export del agente, misma familia de controles que el
            botón CSV del header del reporte. */}
        <div className="flex items-start justify-between gap-3">
          <span className="text-xs text-fg-muted tabular-nums pt-3 md:pt-1.5">
            {loading
              ? t("Cargando…")
              : total === 1
                ? t("1 caso")
                : t("{{count}} casos").replace("{{count}}", formatInt(total))}
          </span>
          <div className="flex flex-col items-end gap-1">
            <button
              type="button"
              onClick={handleCsv}
              disabled={csvState === "downloading" || loading || !showTable || total === 0}
              className={SECONDARY_BTN_CLS}
            >
              {csvState === "downloading" ? (
                <Loader2 className="w-4 h-4 animate-spin" aria-hidden="true" />
              ) : (
                <Download className="w-4 h-4" aria-hidden="true" />
              )}
              {t("Descargar CSV")}
            </button>
            {/* Alto reservado: el error no empuja la tabla. */}
            <p
              className={cn(
                "text-[12px] md:text-[11px] leading-4 h-4 text-status-error text-right",
                csvState !== "error" && "invisible",
              )}
              role={csvState === "error" ? "alert" : undefined}
            >
              {t("No se pudo descargar el CSV.")}
            </p>
          </div>
        </div>

        {backendMissing && (
          <PendingBackendNotice>
            {t("El listado de casos está en construcción.")}
          </PendingBackendNotice>
        )}

        {!backendMissing && loadError && (
          <div className="min-h-[120px] flex flex-col items-center justify-center gap-2 rounded-md border border-border">
            <div className="flex items-center gap-1.5 text-xs text-status-error">
              <AlertTriangle className="w-3.5 h-3.5 shrink-0" aria-hidden="true" />
              {loadError}
            </div>
            <button
              type="button"
              onClick={handleRetry}
              className="h-8 px-3 rounded-md text-xs font-medium text-brand-700 hover:bg-brand-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 transition-colors"
            >
              {t("Reintentar")}
            </button>
          </div>
        )}

        {showTable && (
          // A6 · `ui-table-scroller` (src/scss/_mobile-primitives.scss) pinta el
          // degradado de borde que avisa de que la tabla sigue hacia el lado: a
          // 360px caben 310px de los 655 y sin él las cuatro últimas columnas no
          // dan ninguna señal de existir. Mismo primitivo que la tabla de
          // agentes desde la que se abre este drawer.
          <div className="ui-table-scroller rounded-md border border-border overflow-x-auto overflow-y-auto max-h-[65vh]">
            {/* min-w 560: cabe sin scroll horizontal en el drawer lg (672px
                menos px-6); en pantallas angostas scrollea DENTRO del div. */}
            <table className="w-full min-w-[560px] border-collapse">
              <thead>
                <tr>
                  <th scope="col" className={cn(HEADER_CELL_CLS, "text-left")}>
                    {t("Contacto")}
                  </th>
                  <th scope="col" className={cn(HEADER_CELL_CLS, "text-left")}>
                    {t("Canal")}
                  </th>
                  <th scope="col" className={cn(HEADER_CELL_CLS, "text-right")}>
                    {t("Caso #")}
                  </th>
                  <th scope="col" className={cn(HEADER_CELL_CLS, "text-left")}>
                    {t("Abierto")}
                  </th>
                  <th scope="col" className={cn(HEADER_CELL_CLS, "text-left")}>
                    {t("Resuelto")}
                  </th>
                  <th scope="col" className={cn(HEADER_CELL_CLS, "text-right")}>
                    {t("Duración")}
                  </th>
                </tr>
              </thead>
              <tbody>
                {loading &&
                  Array.from({ length: 4 }).map((_, i) => (
                    <tr key={i} className="h-9 border-b border-border">
                      <td colSpan={6} className="px-3 py-2">
                        <Skeleton className="h-4 w-full rounded" />
                      </td>
                    </tr>
                  ))}

                {!loading && rows.length === 0 && (
                  <tr>
                    <td colSpan={6}>
                      <div className="min-h-[120px] flex items-center justify-center text-xs text-fg-muted">
                        {t("Este agente no tiene casos en el período.")}
                      </div>
                    </td>
                  </tr>
                )}

                {!loading &&
                  rows.map((row) => (
                    <tr
                      key={`${row.conversation_id}-${row.case_number}`}
                      className="h-11 md:h-9 border-b border-border last:border-b-0"
                    >
                      <td className="px-3 text-xs text-fg">
                        <span className="block max-w-[180px] truncate">
                          {row.contact_name || row.contact_phone || "—"}
                        </span>
                      </td>
                      <td className="px-3">
                        <span className="inline-flex items-center rounded bg-status-neutral-soft px-1.5 py-0.5 text-[12px] md:text-[11px] text-fg">
                          {channelLabel(row.channel)}
                        </span>
                      </td>
                      <td className="px-3 text-right text-xs text-fg tabular-nums">
                        #{row.case_number}
                      </td>
                      <td className="px-3 text-xs text-fg tabular-nums whitespace-nowrap">
                        {formatDateTime(row.opened_at)}
                      </td>
                      <td className="px-3 text-xs text-fg tabular-nums whitespace-nowrap">
                        {formatDateTime(row.resolved_at)}
                      </td>
                      <td className="px-3 text-right text-xs text-fg tabular-nums whitespace-nowrap">
                        {formatMinutes(row.duration_min)}
                      </td>
                    </tr>
                  ))}
              </tbody>
            </table>
          </div>
        )}

        {hasMore && (
          <button
            type="button"
            onClick={handleLoadMore}
            disabled={loadingMore}
            className={cn(SECONDARY_BTN_CLS, "w-full")}
          >
            {loadingMore && <Loader2 className="w-4 h-4 animate-spin" aria-hidden="true" />}
            {loadingMore
              ? t("Cargando…")
              : t("Cargar más ({{shown}} de {{total}})", {
                  shown: formatInt(rowsLen),
                  total: formatInt(total),
                })}
          </button>
        )}
      </div>
    </Drawer>
  );
}

// memo: HelpdeskReports lo re-renderiza en cada cambio de estado propio; los
// props son primitivos + onClose estable, así que el memo corta esos renders.
export default memo(AgentCasesDrawer);
